import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useAgencyBillingLock } from "@/hooks/useAgencyBillingLock";
import { AgencyBillingLockScreen } from "@/components/AgencyBillingNotice";
import { ClosedAccountScreen } from "@/components/ClosedAccountScreen";
import { AccountDataExportPanel } from "@/components/AccountDataExportPanel";
import { useBrand } from "@/contexts/BrandContext";
import { LogOut, Building2, ShieldAlert } from "lucide-react";
import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/lib/supabase";
import { useBillingStatus } from "@/hooks/useBillingStatus";
import { useSubscription } from "@/hooks/useSubscription";
import { usePlans } from "@/hooks/usePlans";
import { useBillingSummary } from "@/hooks/useBillingSummary";
import { needsWorkspacePick } from "@/lib/workspacePick";
import { useSupportDesk } from "@/hooks/useSupportDesk";
import { SupportDeskLayout } from "@/components/support-desk/SupportDeskLayout";
import { lazyWithRetry } from "@/lib/lazy-with-retry";
import { accessRuleApplies, paywallDecision } from "@/lib/paywall-access";
import { accountStanding, standingLocksAgencyWorkspace } from "@/lib/account-standing";
import { AccountStandingContext } from "@/contexts/AccountStandingContext";
import { useCurrentBillingAccess } from "@/hooks/useCurrentBillingAccess";
import { useCreditCoverage } from "@/hooks/useCreditCoverage";
import { useOrgPayers } from "@/hooks/useOrgPayers";
import { isOverWithheldCap } from "@/lib/free-quota";
import { isRecargasPlan, zeroQuotaIsCap } from "@/lib/credits-plan";
import { isBillingRecoveryRoute } from "@/lib/billing-recovery-routes";
import { isOverConversationLimit } from "../../supabase/functions/_shared/conversation-limit";

// Background revalidation interval for billing/limit lockouts.
// Catches sessions that were already open when the org went into mora.
const BILLING_REVALIDATE_MS = 3 * 60 * 1000; // 3 minutes

interface OrgLimitData {
  id: string;
  name: string | null;
  conversations_used: number;
  conversations_limit: number;
  billing_cycle_start: string | null;
  created_at: string | null;
  limit_exceeded_at: string | null;
  overage_accepted_at: string | null;
  conversations_extra: number;
  plan: string | null;
  billing_exempt: boolean | null;
  billing_override_until: string | null;
  billing_no_charge?: boolean | null;
  free_quota_withheld?: boolean | null;
  ai_active?: boolean | null;
  manual_block?: boolean | null;
  per_use_authorization_lost_at?: string | null;
  closed_at?: string | null;
}

const SupportDeskVideosPage = lazyWithRetry(() => import("@/pages/support-desk/SupportDeskVideosPage"));

export function ProtectedRoute() {
  const { session, user, loading, subscriptionStatus, signOut, workspaces, switchWorkspace, activeWorkspaceId, userRole } = useAuth();
  const { locked: agencyBillingLocked } = useAgencyBillingLock();
  const brand = useBrand();
  const location = useLocation();
  const supportDesk = useSupportDesk(!loading && !!session && workspaces.length === 0);

  // Conversation limit state
  const [orgLimit, setOrgLimit] = useState<OrgLimitData | null>(null);
  const [limitLoading, setLimitLoading] = useState(true);

  // Fetch org limit data for Flow brand
  const fetchOrgLimit = useCallback(async () => {
    if (!activeWorkspaceId || brand.id !== "flow") {
      setLimitLoading(false);
      return;
    }
    const orgRes = await supabase
      .from("organizations")
      .select("id, name, conversations_used, conversations_limit, conversations_extra, billing_cycle_start, created_at, limit_exceeded_at, overage_accepted_at, plan, billing_exempt, billing_override_until, billing_no_charge, free_quota_withheld, ai_active, manual_block, per_use_authorization_lost_at, closed_at")
      .eq("id", activeWorkspaceId)
      .single();
    if (orgRes.data) setOrgLimit(orgRes.data as unknown as OrgLimitData);
    setLimitLoading(false);
  }, [activeWorkspaceId, brand.id]);

  useEffect(() => { fetchOrgLimit(); }, [fetchOrgLimit]);

  const overQuota = brand.id === "flow" && !!orgLimit &&
    (zeroQuotaIsCap(orgLimit) ? isOverWithheldCap(orgLimit) : isOverConversationLimit(orgLimit));
  const creditsApply = overQuota || (brand.id === "flow" && isRecargasPlan(orgLimit?.plan));
  const { coverage: creditCoverage, loading: creditCoverageLoading } = useCreditCoverage(
    activeWorkspaceId,
    creditsApply,
    orgLimit?.conversations_used,
  );
  const creditsPay = creditCoverage?.covers === true;
  const creditsExhausted = creditCoverage?.exhausted === true;
  const needsLimitStamp = brand.id === "flow" && !!orgLimit && !orgLimit.limit_exceeded_at && isOverConversationLimit(orgLimit) &&
    !creditCoverageLoading && !creditsPay && !creditsExhausted;
  useEffect(() => {
    if (!needsLimitStamp) return;
    void Promise.resolve(supabase.rpc("mark_conversation_limit_exceeded" as never)).then(() => fetchOrgLimit());
  }, [needsLimitStamp, fetchOrgLimit]);

  // Billing status for custom & per_use plans (overdue lockout)
  const billing = useBillingStatus(activeWorkspaceId, orgLimit?.plan ?? null);
  const { subscription, refetch: refetchSubscription } = useSubscription(activeWorkspaceId);
  const { plans: allPlans } = usePlans(true);
  const billingPlan = allPlans.find(p => p.id === (orgLimit?.plan ?? "")) ?? null;
  const billingSummary = useBillingSummary(activeWorkspaceId, subscription, billingPlan, orgLimit?.conversations_used ?? null, orgLimit?.billing_cycle_start ?? null, orgLimit?.created_at ?? null);
  const { access: billingAccess, loading: billingAccessLoading, refresh: refreshBillingAccess } = useCurrentBillingAccess(
    activeWorkspaceId,
    accessRuleApplies(subscriptionStatus, brand.id),
  );
  const paywall = paywallDecision({
    subscriptionStatus,
    brandId: brand.id,
    organizationId: activeWorkspaceId,
    access: billingAccess,
    accessLoading: billingAccessLoading,
  });
  const standing = accountStanding({
    brandId: brand.id,
    subscriptionStatus,
    organizationId: activeWorkspaceId,
    access: billingAccess,
    accessLoading: billingAccessLoading,
    org: orgLimit,
    billing: {
      loading: billing.loading,
      isCustomPlan: billing.isCustomPlan,
      billingMode: billing.currentPlan?.billing_mode ?? null,
      billingState: billing.billingState,
      graceDeadline: billing.graceDeadline,
      subscriptionEnd: billing.subscriptionEnd,
    },
    credits: creditsApply
      ? { loading: creditCoverageLoading, covers: creditsPay, exhausted: creditsExhausted, owedCop: creditCoverage?.owedCop ?? null }
      : { loading: false, covers: false, exhausted: false },
    owed: {
      access: billingAccess?.owed_total ?? null,
      overview: billingSummary.overview?.owedTotal ?? null,
      payableCycle: billingSummary.overview?.payableCycle?.amount ?? null,
      estimated: billingSummary.totalEstimated ?? null,
    },
  });
  const accountClosed = brand.id === "flow" ? paywall.kind === "locked" && standing.kind === "closed" : paywall.kind === "locked";
  const closedScreenShown = brand.id === "flow" && accountClosed && standing.kind === "closed";
  const { payers: closedAccountAdmins, loading: closedAccountAdminsLoading } = useOrgPayers(
    activeWorkspaceId,
    closedScreenShown && userRole !== "admin",
  );

  // ── Re-validation layer 1: refetch when route changes ──
  // Catches users navigating between conversations after billing went into mora.
  const lastRouteRef = useRef(location.pathname);
  useEffect(() => {
    if (lastRouteRef.current !== location.pathname) {
      lastRouteRef.current = location.pathname;
      fetchOrgLimit();
      refetchSubscription();
      refreshBillingAccess();
    }
  }, [location.pathname, fetchOrgLimit, refetchSubscription, refreshBillingAccess]);

  // ── Re-validation layer 2: periodic background polling ──
  useEffect(() => {
    if (!activeWorkspaceId || brand.id !== "flow") return;
    const id = setInterval(() => {
      fetchOrgLimit();
      refetchSubscription();
      refreshBillingAccess();
    }, BILLING_REVALIDATE_MS);
    return () => clearInterval(id);
  }, [activeWorkspaceId, brand.id, fetchOrgLimit, refetchSubscription, refreshBillingAccess]);

  // ── Re-validation layer 3: realtime on organizations + subscriptions ──
  useEffect(() => {
    if (!activeWorkspaceId || brand.id !== "flow") return;
    const channel = supabase
      .channel(`billing-guard-${activeWorkspaceId}`)
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "organizations", filter: `id=eq.${activeWorkspaceId}` },
        () => { fetchOrgLimit(); refreshBillingAccess(); }
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "subscriptions", filter: `organization_id=eq.${activeWorkspaceId}` },
        () => { refetchSubscription(); refreshBillingAccess(); }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [activeWorkspaceId, brand.id, fetchOrgLimit, refetchSubscription, refreshBillingAccess]);


  if (loading || supportDesk.loading || (brand.id === "flow" && limitLoading && !supportDesk.isSupportDeskAgent) || paywall.kind === "loading") {
    // Unlike GlobalLoadingBar/DelayedSpinner, this one shows immediately —
    // it's the very first thing rendered (session/workspace check), so
    // there's nothing else on screen yet for a delay to protect against.
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  if (supportDesk.isSupportDeskAgent) {
    if (location.pathname === "/app/help/videos" && supportDesk.supportDeskBrand === brand.id) {
      return (
        <SupportDeskLayout>
          <SupportDeskVideosPage />
        </SupportDeskLayout>
      );
    }
    return <Navigate to={`/support-desk${location.search}`} replace />;
  }

  // DE-653: force post-login workspace choice before entering the app.
  if (needsWorkspacePick() && workspaces.length >= 2) {
    return <Navigate to="/select-workspace" replace />;
  }

  // Force password change for API-created users
  if (user?.user_metadata?.must_change_password === true && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }

  if (brand.id === "flow" && accountClosed && standing.kind === "closed") {
    const otherWorkspaces = workspaces.filter(w => w.organization_id !== activeWorkspaceId);
    return (
      <ClosedAccountScreen
        reason={standing.reason}
        orgName={orgLimit?.name ?? null}
        closedAt={orgLimit?.closed_at ?? null}
        isAdmin={userRole === "admin"}
        admins={closedAccountAdmins}
        adminsLoading={closedAccountAdminsLoading}
        whatsappNumber={brand.whatsappWidgetNumber}
        downloadAction={<AccountDataExportPanel organizationId={activeWorkspaceId} compact />}
        onSwitchWorkspace={otherWorkspaces.length > 0 ? () => switchWorkspace(otherWorkspaces[0].organization_id) : undefined}
        onSignOut={signOut}
      />
    );
  }

  if (paywall.kind === "locked" && (accountClosed || agencyBillingLocked)) {
    const isManualBlock = paywall.reason === "manual_block" || orgLimit?.manual_block === true;
    const isSuspended = isManualBlock || paywall.reason !== "terminated";
    const isFlow = brand.id === "flow";
    const otherWorkspaces = workspaces.filter(w => w.organization_id !== activeWorkspaceId);

    if (agencyBillingLocked) {
      return (
        <AgencyBillingLockScreen
          title={isSuspended ? "Tu cuenta está pausada" : "Cuenta cancelada"}
          message="El acceso a este workspace está pausado por el estado de su facturación. Tus datos están seguros."
          onSwitchWorkspace={otherWorkspaces.length > 0 ? () => switchWorkspace(otherWorkspaces[0].organization_id) : undefined}
          onSignOut={signOut}
        />
      );
    }

    return (
      <div className="flex h-screen w-full items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-5 max-w-md text-center p-8">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-destructive/10">
            <ShieldAlert className="h-10 w-10 text-destructive" />
          </div>
          <div>
            <h1 className="text-2xl font-bold font-display text-foreground mb-2">
              {!isSuspended
                ? "Cuenta cancelada"
                : isManualBlock
                  ? "Tu cuenta está pausada"
                  : "Cuenta suspendida por falta de pago"}
            </h1>
            <p className="text-muted-foreground text-sm">
              {!isSuspended
                ? "Tu cuenta ha sido cancelada. Contacta a tu proveedor de servicio para más información."
                : isManualBlock
                  ? "Nuestro equipo pausó tu cuenta. Tus datos están seguros. Escríbenos para revisar el caso y reactivarla."
                  : "Tu suscripción venció y el acceso a la plataforma fue bloqueado. Tus datos están seguros y se restaurarán cuando regularices tu pago."}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 w-full">
            {otherWorkspaces.length > 0 && (
              <button
                onClick={() => switchWorkspace(otherWorkspaces[0].organization_id)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-border text-sm font-medium hover:bg-accent transition-colors"
              >
                <Building2 className="h-4 w-4" /> Cambiar workspace
              </button>
            )}
            <button
              onClick={signOut}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-border text-sm font-medium hover:bg-accent transition-colors"
            >
              <LogOut className="h-4 w-4" /> Cerrar sesión
            </button>
          </div>

          {isFlow && isSuspended && (
            <p className="text-xs text-muted-foreground mt-2">
              ¿Tienes problemas? Escríbenos a{" "}
              <a href={`mailto:${brand.supportEmail}`} className="text-primary underline">{brand.supportEmail}</a>
            </p>
          )}
        </div>
      </div>
    );
  }

  // Guard: wait until activeWorkspaceId is resolved
  if (!activeWorkspaceId) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">Cargando workspace...</p>
          <p className="text-xs text-muted-foreground max-w-xs text-center">
            Si esta pantalla persiste, puede ser un problema de sesión.
          </p>
          <button
            onClick={signOut}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-accent transition-colors"
          >
            <LogOut className="h-4 w-4" /> Cerrar sesión y reintentar
          </button>
        </div>
      </div>
    );
  }

  // ── HOST ROLE RESTRICTION — only conversations and reservations ──
  const isHostRole = userRole === "host";
  const hostAllowed = location.pathname.startsWith("/app/conversations") || location.pathname.startsWith("/app/reservations") || location.pathname.startsWith("/app/business");
  if (isHostRole && !hostAllowed) {
    return <Navigate to="/app/conversations" replace />;
  }

  const isAccountPage = location.pathname.startsWith("/app/settings") || isBillingRecoveryRoute(location.pathname);
  const isSuperAdminRoute = location.pathname.startsWith("/super-admin");

  if (
    brand.id === "flow" &&
    agencyBillingLocked &&
    !isAccountPage &&
    !isSuperAdminRoute &&
    standingLocksAgencyWorkspace(standing)
  ) {
    const otherWs = workspaces.filter(w => w.organization_id !== activeWorkspaceId);
    const overLimit = standing.kind !== "ok" && standing.kind !== "closed" &&
      (standing.reason === "over_limit" || standing.reason === "credits_exhausted");
    return (
      <AgencyBillingLockScreen
        title={overLimit ? "Este workspace llegó a su límite de conversaciones" : "Acceso pausado"}
        message={overLimit
          ? "La IA y las automatizaciones de este workspace están en pausa por el estado de su facturación."
          : "El acceso a este workspace está pausado por el estado de su facturación. Tus datos están seguros."}
        onSwitchWorkspace={otherWs.length > 0 ? () => switchWorkspace(otherWs[0].organization_id) : undefined}
        onSignOut={signOut}
      />
    );
  }

  return (
    <AccountStandingContext.Provider value={brand.id === "flow" ? standing : null}>
      <Outlet />
    </AccountStandingContext.Provider>
  );
}
